<script setup>
  import {computed, inject, ref, watch} from "vue";

  import ComponentRowOriginFromMergeBadge from "@components/Badges/RowOriginFromMergeBadge.vue";
  import ComponentFakeInput from "@components/Inputs/FakeInput.vue";
  import ComponentFullDateInput from "@components/Inputs/FullDateInput.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {router, usePage} from "@inertiajs/vue3";
  import {bloodpressureKey, laborbardeviationValues} from "@pages/Records/Components/Sections/LaborBar/enums.js";
  import {
    getDeviationFromBloodpressureValue,
    getDeviationFromValue,
    getReferenceText,
    getReferenceValue,
  } from "@pages/Records/Components/Sections/LaborBar/Utils/laborvalue.js";
  import {MedButton, MedIcon, MedTooltip} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import NumberHelper from "@utils/Helpers/NumberHelper.js";

  defineOptions({
    name: "LaborTableRow",
  });

  const props = defineProps({
    isEditable: {
      type: Boolean,
      default: false,
    },
    laborvalue: {
      type: Object,
      required: true,
    },
    dragable: {
      type: Boolean,
      default: true,
    },
    deletable: {
      type: Boolean,
      default: true,
    },
    showLabels: {
      type: Boolean,
      default: true,
    },
    isDragging: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["update"]);

  const page = usePage();

  const deleteDialog = inject("recordEditDeleteRowDialog");

  const form = ref(props.laborvalue);

  const fieldsDisabled = computed(() => !props.isEditable || !form.value.core.mc_laborvalue_key);

  const referenceValue = computed(() => getReferenceValue(form.value, page.props.patient.gender));

  const referenceText = computed(() => {
    return referenceValue.value && form.value.unit ? getReferenceText(referenceValue.value) : null;
  });

  const showValueLabel = computed(() => (props.showLabels ? "Wert (opt.):" : ""));

  const showUnitLabel = computed(() => (props.showLabels ? "Einheit (opt.):" : ""));

  const showDeviationLabel = computed(() => (props.showLabels ? "Abweichung: " : ""));

  const showDateLabel = computed(() => (props.showLabels ? "Von (Datum): " : ""));

  watch(
    () => props.laborvalue,
    () => {
      form.value = props.laborvalue;
      // call once to force correct deviation after importing a record
      // note: bind this logic to the data, not to the component (onMounted)
      calculateDeviation(true);
    },
    {
      deep: true,
      immediate: true, // force to run at least once when the data is set
    },
  );

  const formatValue = (value) => {
    // Add Slash to Bloodpressure
    if (bloodpressureKey !== form.value.mc_laborvalue_key) {
      return NumberHelper.formatFloatAsGermanNumberString(NumberHelper.formatStringAsFloat(value));
    }

    // Skip if is empty or valid value
    if (!value || value.length === 0 || /^[0-9]{2,3} \/ [0-9]{2,3}$/.test(value)) return value;

    if ((value.length === 2 && Number.parseInt(value.slice(0, 1)) > 3) || value.length === 3) {
      let lastChar = value.slice(-1);

      if (/[0-9]/.test(lastChar)) {
        return value + " / ";
      } else {
        return value.slice(0, 2) + " / ";
      }
    } else if (value.length > 3) {
      let matches = value.match(/([0-9]{2,3})[^0-9]+([0-9]{0,2})/);

      if (matches) {
        return matches[1] + " / " + (matches[2] ?? "");
      }
    }

    return value;
  };

  const updateValue = (newValue) => {
    if (newValue && newValue !== "") {
      const formattedValue = formatValue(newValue.toString());
      if (form.value.value !== formattedValue) {
        form.value.value = formattedValue;
        emit("update");
      }

      if (!form.value.unit && form.value.core.unitList?.length > 0) {
        form.value.mc_unit_id = form.value.core.unitList[0].id;
        form.value.unit = form.value.core.unitList[0].unit;
      }
    } else {
      if (form.value.value !== null) {
        form.value.value = null;
        emit("update");
      }
    }

    calculateDeviation();
  };

  // note: avoid const here to avoid temporal dead zone (the
  // method can be called from code above, when it is
  // declared as a normal hoisted function)
  function calculateDeviation(onlyWhenNotAlreadySet) {
    if (
      onlyWhenNotAlreadySet !== undefined &&
      onlyWhenNotAlreadySet === true && // early exit if deviation is already set
      form.value?.deviation !== null
    ) {
      return;
    }

    let oldValue = form.value?.deviation;

    if (referenceValue.value && form.value.value !== null && form.value.value !== "") {
      if (form.value.mc_laborvalue_key === bloodpressureKey) {
        form.value.deviation = getDeviationFromBloodpressureValue(
          form.value.value,
          referenceValue.value.low,
          referenceValue.value.high,
        );
      } else {
        form.value.deviation = getDeviationFromValue(
          form.value.value,
          referenceValue.value.low,
          referenceValue.value.high,
        );
      }
    } else {
      form.value.deviation = "normal";
    }

    if (oldValue !== form.value.deviation) {
      // deviation is "dirty", so we have to update the value
      emit("update");
    }
  }

  const resetDeviation = () => {
    form.value.deviation = "normal";
    emit("update");
  };

  const updateUnit = (newUnit) => {
    // reset value
    form.value.value = null;
    // reset deviation
    form.value.deviation = "normal";

    form.value.mc_unit_id = newUnit.id;
    form.value.unit = newUnit.unit;

    emit("update");
  };

  const showDeleteConfirmDialog = () => {
    deleteDialog({routeName: "laborvalues", payload: {laborvalue: form.value.id}});
  };

  const updateDate = (newDate) => {
    form.value.date = newDate;
    emit("update");
  };

  const patchLaborvalue = (overrides = {}) => {
    router.patch(
      route("laborvalues.update", {
        patient: page.props.patient.id,
        record: page.props.record.id,
        laborvalue: form.value.id,
      }),
      {
        type: form.value.type,
        deviation: form.value.deviation,
        date: form.value.date,
        value: form.value.value,
        unit: form.value.unit,
        mc_unit_id: form.value.mc_unit_id,
        mc_laborvalue_id: form.value.mc_laborvalue_id,
        mc_laborvalue_key: form.value.mc_laborvalue_key,
        row: form.value.row,
        ...overrides,
      },
      {
        preserveScroll: true,
        only: ["flash", "errors", "laborvalues"],
      },
    );
  };

  const clearDate = () => {
    form.value.date = null;
    patchLaborvalue({date: null});
  };

  const updateDeviation = (newDeviation) => {
    // delete an existing value if deviation is set manually (!) to a contrary value
    if (props.laborvalue.deviation !== newDeviation) {
      form.value.value = "";
    }

    form.value.deviation = newDeviation;
    emit("update");
  };
</script>

<template>
  <li class="grid grid-cols-13 gap-x-6 py-2">
    <!-- row 0 - optional origin when merging records -->
    <ComponentRowOriginFromMergeBadge :hashid="form.id" type="laborvalues" class="col-span-12 ml-8" />

    <!-- row 1 - col 1 - "Was" + drag and drop icon -->
    <div class="col-span-3" :class="{'h-6': showLabels}">
      <template v-if="showLabels">
        <span :class="cn('origin-0 -z-1 cursor-default truncate text-sm text-gray-500', {'ml-8': $slots.prefix})">
          Was:
        </span>
      </template>

      <div class="flex items-center">
        <template v-if="isEditable">
          <slot name="prefix">
            <MedTooltip v-if="dragable" side="left" content="Zeile verschieben" :disabled="isDragging">
              <span
                role="button"
                tabindex="0"
                aria-label="Zeile verschieben"
                class="flex cursor-grab items-center active:cursor-grabbing"
                drag-handle
                @keydown.enter.prevent
                @keydown.space.prevent
              >
                <MedIcon size="lg" icon="drag_handle" />
              </span>
            </MedTooltip>
          </slot>
        </template>

        <ComponentFakeInput
          class="ml-2 w-full"
          :class="{'ml-1': dragable}"
          :value="form?.type ?? ''"
          :disabled="!isEditable"
          :test-id="getTestId('row-name')"
        />
      </div>
    </div>

    <!-- row 1 - col 2 - "Wert (OPT)" -->
    <div class="col-span-3">
      <ComponentInput
        :model-value="form.value"
        :helper-text="referenceText"
        :disabled="fieldsDisabled"
        :label="showValueLabel"
        :test-id="getTestId('row-value')"
        @input="updateValue"
        @clear="resetDeviation"
      />
    </div>

    <!-- row 1 - col 3 - "Einheit (OPT)" -->
    <div class="col-span-2">
      <ComponentSelect
        :id="'unit' + form.id"
        :model-value="form.mc_unit_id"
        :disabled="fieldsDisabled"
        :options="form.core.unitList"
        :label="showUnitLabel"
        key-value="id"
        key-name="unit"
        return-type="object"
        :test-id="getTestId('row-unit')"
        @change="updateUnit"
      />
    </div>

    <!-- row 1 - col 4 - "Abweichung (OPT)" -->
    <div class="col-span-2" :class="[{'col-span-3': !isEditable || !deletable}]">
      <ComponentSelect
        :id="'deviation' + form.id"
        :model-value="form.deviation"
        :label="showDeviationLabel"
        :disabled="fieldsDisabled"
        :options="laborbardeviationValues"
        key-name="name"
        :test-id="getTestId('row-deviation')"
        @change="updateDeviation"
      />
    </div>

    <!-- row 1 - col 5 - "Von (Datum)" -->
    <div class="col-span-2" :class="{'col-span-1': isEditable && deletable}">
      <ComponentFullDateInput
        :model-value="form.date"
        :disabled="fieldsDisabled"
        :label="showDateLabel"
        :test-id="getTestId('row-date')"
        @input="updateDate"
        @clear="clearDate"
      />
    </div>

    <!-- row 1 - col 6 - "Actions" -->
    <template v-if="isEditable && deletable">
      <div class="col-span-1 items-baseline px-2" :class="showLabels ? 'pt-6' : 'pt-1'">
        <MedTooltip content="Zeile löschen">
          <MedButton
            :aria-label="form.type ? `Laborwert ${form.type} löschen` : 'Laborwert löschen'"
            flat
            icon="delete"
            rounded="full"
            :data-test="getTestId('row-delete-button')"
            @click="showDeleteConfirmDialog"
          />
        </MedTooltip>
      </div>
    </template>
  </li>
</template>
